import React, { useState, useEffect } from 'react';
import api from './config/axios';
import Auth from './components/auth/Auth';
import Dashboard from './components/dashboard/Dashboard';
import Menu from './components/menu/Menu';
import Footer from './components/common/Footer';
import EmployeeManagement from './components/employee/EmployeeManagement';
import MaterialManagement from './components/material/MaterialManagement';
import ShopSettings from './components/settings/ShopSettings';
import AccountSettings from './components/settings/AccountSettings';
import ShiftManagement from './components/shift/ShiftManagement';
import OrderCreate from './components/order/OrderCreate';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AllOrders from './components/order/AllOrders';
import OrderPayment from './components/order/OrderPayment';
import Report from './components/report/Report';
import SalaryManagement from './components/salary/SalaryManagement';
import MySalary from './components/salary/MySalary';
import TableMap from './components/table/TableMap';
import ReservedTableList from './components/table/ReservedTableList';
import TableManagement from './components/table/TableManagement';
import MyReservations from './components/table/MyReservations';
import OrderAfterReservation from './components/order/OrderAfterReservation';
import OrderCreateByQR from './components/order/OrderCreateByQR';
import OrderGuestPayment from './components/order/OrderGuestPayment';
import PaymentCallback from './components/payment/PaymentCallback';
import GroupOrderCreate from './components/order/GroupOrderCreate';
import GroupOrderJoin from './components/order/GroupOrderJoin';
import GroupOrder from './components/order/GroupOrder';
import GroupOrderPayment from './components/order/GroupOrderPayment';
import GroupPaymentSuccess from './components/order/GroupPaymentSuccess';
import OrderLanding from './components/order/OrderLanding';
import { NotificationProvider } from './contexts/NotificationContext';
import SocketManager from './components/common/SocketManager';
import SocketConnectionManager from './components/common/SocketConnectionManager';
import './styles/global.css';

interface User {
  id: string; 
  username: string; 
  email: string;   
  role: string; 
}

function App() {
  // Khôi phục phiên đăng nhập ngay ở lần render đầu (đồng bộ),
  // tránh việc reload bị đá về /login rồi về trang chủ.
  const [user, setUser] = useState<User | null>(() => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      return token && userData ? (JSON.parse(userData) as User) : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      return !!(token && userData && JSON.parse(userData));
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
 
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Invalid user data in localStorage:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  const handleLoginSuccess = (token: string, userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (
      !isAuthenticated &&
      location.pathname !== '/order' &&
      location.pathname !== '/order/create' &&
      location.pathname !== '/orders/guest-payment' &&
      location.pathname !== '/orders/group-create' &&
      location.pathname !== '/orders/group-join' &&
      location.pathname !== '/orders/group-order' &&
      location.pathname !== '/orders/group-payment' &&
      location.pathname !== '/orders/group-payment-success'
    ) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate, location.pathname]);

  return (
    <NotificationProvider>
      <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <SocketManager isAuthenticated={isAuthenticated} user={user} />
        
        {isAuthenticated && user && (user.role === 'employee' || user.role === 'admin') && (
          <SocketConnectionManager user={user} />
        )}
        
        <div className="app-content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route path="/order/create" element={<OrderCreateByQR />} />
            <Route path="/order" element={<OrderLanding />} />
            <Route path="/orders/guest-payment" element={<OrderGuestPayment />} />
            <Route path="/orders/group-create" element={<GroupOrderCreate />} />
            <Route path="/orders/group-join" element={<GroupOrderJoin />} />
            <Route path="/orders/group-order" element={<GroupOrder />} />
            <Route path="/orders/group-payment" element={<GroupOrderPayment />} />
            <Route path="/orders/group-payment-success" element={<GroupPaymentSuccess />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/login" element={
              isAuthenticated && user ? <Navigate to="/" /> : <Auth onLoginSuccess={handleLoginSuccess} />
            } />

            {isAuthenticated && user ? (
              <>
                <Route path="/" element={<Dashboard user={user} onLogout={handleLogout} />} />
                <Route path="/menu" element={<Menu user={user} />} />
                <Route path="/account" element={<AccountSettings user={user} />} />
                {user.role === 'admin' && (
                  <Route path="/employees" element={<EmployeeManagement user={user} />} />
                )}
                {user.role === 'admin' && (
                  <Route path="/materials" element={<MaterialManagement user={user} />} />
                )}
                {user.role === 'admin' && (
                  <Route path="/settings" element={<ShopSettings user={user} />} />
                )}
                <Route path="/shifts" element={<ShiftManagement user={user} onLogout={handleLogout} />} />
                <Route path="/orders/new" element={<OrderCreate user={user} />} />
                <Route path="/orders" element={<AllOrders user={user} onLogout={handleLogout} />} />
                <Route path="/orders/payment" element={<OrderPayment user={user} />} />
                <Route path="/reports" element={<Report user={user} />} />
                {user.role === 'admin' && (
                  <Route path="/salaries" element={<SalaryManagement user={user} />} />
                )}
                <Route path="/my-salary" element={<MySalary user={user} />} />
                {user.role === 'customer' && (
                  <Route path="/tables" element={<TableMap user={user} onLogout={handleLogout} />} />
                )}
                {user.role === 'customer' && (
                  <Route path="/my-reservations" element={<MyReservations user={user} onLogout={handleLogout} />} />
                )}
                {(user.role === 'employee' || user.role === 'admin') && (
                  <Route path="/tables" element={<ReservedTableList user={user} onLogout={handleLogout} />} />
                )}
                {user.role === 'admin' && (
                  <Route path="/tables/manage" element={<TableManagement />} />
                )}
                {user.role === 'customer' && (
                  <Route path="/order/reserve/:reservationId" element={<OrderAfterReservation />} />
                )}
                <Route path="*" element={<Navigate to="/" />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/login" />} />
            )}
          </Routes>
        </div>
        
        {isAuthenticated && user && <Footer />}
      </div>
    </NotificationProvider>
  );
}

export default App;
